import { useContext } from "react";
import { Box, Checkbox, Typography } from "@mui/material";

import type {
  FormContextData,
  FormContextMiniForm,
  SelectedFormsObject,
} from "@thalamos/aspire-common";

import { LoggedInUserContext } from "../../Contexts.js";
import { AdmissionFormRow } from "../FormProgressPage/admission/AdmissionFormRow.js";

export function FormCheckboxRow({
  setSelectedForms,
  setPdfViewFormId,
  form,
  formContext,
  checked,
  selectable = true,
  isMultiSelect,
  isSingleSelectCheckBox,
  lastMerged,
  selectionOrder,
  onUserDeselect,
}: {
  setSelectedForms: (params: any) => void;
  setPdfViewFormId: (params: any) => void;
  form: FormContextMiniForm;
  formContext: Omit<FormContextData, "patient">;
  checked: boolean;
  // Whether this form may be chosen as a basis. When false it is still shown,
  // but as inert context: greyed and non-interactive rather than a live choice.
  selectable?: boolean;
  isMultiSelect: boolean;
  isSingleSelectCheckBox: boolean;
  lastMerged?: string;
  selectionOrder?: number;
  // Called when the user explicitly unticks this row, so the parent can stop
  // auto-re-selecting a single available form.
  onUserDeselect?: () => void;
}) {
  const userContext = useContext(LoggedInUserContext);
  return (
    <Box
      data-testid="supporting-form-row"
      sx={{
        display: "flex",
        alignItems: "center",
      }}
    >
      {isMultiSelect ? (
        <>
          {selectionOrder && <Typography>{selectionOrder}</Typography>}
          <Checkbox
            sx={{ color: "primary.main", maxHeight: 100 }}
            data-testid={`form-row-checkbox:${form.id}`}
            disabled={!selectable}
            onClick={() => {
              if (isSingleSelectCheckBox) {
                setSelectedForms((prevSelectedForms: SelectedFormsObject[]) => {
                  const isSelected = prevSelectedForms.some(
                    (item) => item.id === form.id,
                  );
                  if (isSelected) {
                    onUserDeselect?.();
                    return [];
                  } else {
                    return [
                      {
                        id: form.id,
                        templateId: form.formTemplate.id,
                      },
                    ];
                  }
                });
              } else {
                if (checked) {
                  onUserDeselect?.();
                  setSelectedForms(
                    (prevSelectedForms: SelectedFormsObject[]) => {
                      return prevSelectedForms.filter(
                        (item) => item.id !== form.id,
                      );
                    },
                  );
                } else {
                  setSelectedForms(
                    (prevSelectedForms: SelectedFormsObject[]) => {
                      return [
                        ...prevSelectedForms,
                        {
                          id: form.id,
                          templateId: form.formTemplate.id,
                        },
                      ];
                    },
                  );
                }
              }
            }}
            checked={checked}
          />
        </>
      ) : (
        <Checkbox
          sx={{ color: "primary.main", maxHeight: 100 }}
          data-testid={`form-row-checkbox:${form.id}`}
          disabled={!selectable}
          onClick={() => {
            if (checked) {
              onUserDeselect?.();
              setSelectedForms([]);
            } else {
              setSelectedForms([
                {
                  id: form.id,
                  templateId: form.formTemplate.id,
                },
              ]);
            }
          }}
          checked={checked}
        />
      )}
      <AdmissionFormRow
        setPdfViewFormId={setPdfViewFormId}
        form={form}
        formContext={formContext!}
        user={userContext?.user!}
        createDraftFn={() => Promise.resolve()}
        lastMerged={lastMerged}
        greyedOut={!selectable}
        // We do not need to pass the timeline in this scenario as it is
        // used only for determining amend status in data edge cases, and this
        // should never apply here (ASP-1605)
        patientTimeline={null}
      />
    </Box>
  );
}
